import React from "react";
import { Link } from "react-router-dom";
import { Github, Linkedin, Mail } from "lucide-react";
import { siteConfig } from "@/data/site";
import GitPrompt from "@/components/GitPrompt";
import { useTimeTheme } from "@/hooks/use-time-theme";
import GitHubCommitCount from "@/components/GitHubCommitCount";

const Index = () => {
  const theme = useTimeTheme();

  return (
    <div className={`min-h-screen ${theme.background} ${theme.text} transition-colors duration-1000`}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header - Simple and direct */}
        <header className="pt-8 sm:pt-12 md:pt-16 pb-16 sm:pb-20 md:pb-24">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <Link
              to="/"
              className={`text-xl sm:text-2xl md:text-3xl font-bold ${theme.accent} hover:opacity-80 transition-all text-center sm:text-left`}
            >
              {siteConfig.name.split(" ")[0].toLowerCase()}.space
            </Link>
            
            {/* Navigation - Centered on mobile, right-aligned on large screens */}
            <nav className={`flex flex-wrap items-center justify-center sm:justify-end gap-4 sm:gap-6 text-sm sm:text-base ${theme.accent}/80`}>
              <Link to="/about" className="hover:opacity-100 transition-colors">
                About
              </Link>
              <Link to="/blog" className="hover:opacity-100 transition-colors">
                Blog
              </Link>
              <Link to="/projects" className="hover:opacity-100 transition-colors">
                Projects
              </Link>
              <Link to="/uses" className="hover:opacity-100 transition-colors">
                Uses
              </Link>
              <Link to="/opensource" className="hover:opacity-100 transition-colors">
                Open Source
              </Link>
            </nav>
          </div>
        </header>

        {/* Main Content */}
        <main className="pb-12 sm:pb-16">
          <section className="flex flex-col items-center">
            <div className="max-w-2xl w-full space-y-4 sm:space-y-5 text-center">
              {/* Git Prompt */}
              <GitPrompt command="cat README.md" className="justify-center" />

              {/* Greeting - Large and prominent */}
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold leading-tight tracking-tighter sm:whitespace-nowrap">
                Hey there, I&apos;m {siteConfig.name.split(" ")[0]}! 👋
              </h1>
              
              {/* Introduction text */}
              <div className={`space-y-4 text-base sm:text-lg md:text-xl ${theme.text}/80 leading-relaxed`}>
                <p>
                  Software Engineer. Currently building cloud security products at{" "}
                <a 
                  href="https://www.cyberdesk.app/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                    className={`${theme.accent} hover:opacity-80 underline transition-colors`}
                >
                    CyberDesk GmbH
                  </a>
                  .
                </p>
                <p>
                  Usually found building tools, writing about{" "}
                
                    systems
                  , or working on my motorcycles.
                </p>
              </div>
            </div>
          </section>

          {/* Social Links and Footer - Centered */}
          <section className={`mt-16 sm:mt-20 md:mt-24 flex flex-col items-center gap-6 ${theme.text}/60`}>
            {/* GitHub Commit Count with Progress Indicator */}
            <GitHubCommitCount />
            
            <div className="flex items-center gap-6">
              <a
                href={siteConfig.social.github}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="GitHub"
                className="transition-colors hover:opacity-100"
              >
                <Github className="h-6 w-6 sm:h-7 sm:w-7" />
              </a>
              <a
                href={siteConfig.social.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className="transition-colors hover:opacity-100"
              >
                <Linkedin className="h-6 w-6 sm:h-7 sm:w-7" />
              </a>
              <a
                href={`mailto:${siteConfig.email}`}
                aria-label="Email"
                className="transition-colors hover:opacity-100"
              >
                <Mail className="h-6 w-6 sm:h-7 sm:w-7" />
              </a>
            </div>
            <p className="text-xs sm:text-sm flex flex-wrap items-center justify-center gap-2">
              <span>&gt; built with</span>
              <span className="inline-flex h-4 overflow-hidden align-middle">
                <span className="rotate-words">
                  <span>Cursor</span>
                  <span>love</span>
                  <span>coffee</span>
                  <span>code</span>
                </span>
              </span>
            </p>
          </section>
        </main>
        </div>
    </div>
  );
};

export default Index;
